import React, {useContext, useEffect, useState} from "react";
import {Checkbox, Form, Input, Select, Alert, Steps, Divider} from "antd";
import Button from "../../common/Button";
import {UserContext} from "../../common/UserContext";
import {BrandContext} from "../../common/BrandContext";
import axios from "../../common/axios";
import {NavigationContext} from "../../common/NavigationContext";
import UserSelector from "../components/UserSelector";


const AddClassDrawer = () => {
    const [options, setOptions] = useState({});
    const [formError, setFormError] = useState(null);
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const [addClassForm] = Form.useForm();
    const { setDrawerVisible } = useContext(NavigationContext);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);

    const closeDialog = () => {
        setDrawerVisible(false);
        setFormError(null);
    };

    const handleContinue = async () => {
        try {
            await addClassForm.validateFields([['clazz', 'className']]);
            setCurrentStep(1);
        } catch (error) {
        }
    };

    const handleBack = () => {
        setCurrentStep(0);
    };

    useEffect(() => {
        axios.get('/web/ui/sc/getOptions')
            .then(response => {
                setOptions(response.data);
            })
            .catch(error => {
                console.error("Failed to fetch options", error);
            });
    }, []);

    useEffect(() => {
        if (Object.keys(options).length !== 0) {
            const initialValues = {
                clazz: {
                    calendarYear: new Date().getFullYear(),
                    schoolYear: options?.schoolYears?.[7]?.value,
                    hideWorkspaces: false,
                    showWorkedSolutions: false
                }
            };

            if (brandConfig?.questionClusterMarkingTypes && brandConfig.questionClusterMarkingTypes.length > 0) {
                initialValues.clazz.markingType = brandConfig.questionClusterMarkingTypes[0];
            }

            if (brandConfig?.showSolutions !== undefined) {
                initialValues.clazz.showSolutions = brandConfig.showSolutions;
            } else {
                initialValues.clazz.showSolutions = false;
            }

            addClassForm.setFieldsValue(initialValues);
        }
    }, [options, brandConfig, addClassForm]);

    const handleSave = async () => {
        setFormError(null);

        try {
            await addClassForm.validateFields();

            const addClassFormValues = addClassForm.getFieldsValue(true);

            const response = await axios.post('/web/ui/sc/newClass', addClassFormValues.clazz, {
                headers: { 'Content-Type': 'application/json' }
            });

            if (selectedUsers.length > 0) {
                try {
                    const allClasses = response.data;

                    const newlyCreatedClass = Object.values(allClasses).find(
                        clazz => clazz.className === addClassFormValues.clazz.className
                    );

                    if (newlyCreatedClass && newlyCreatedClass.classId) {
                        const userIds = selectedUsers.map(item => item.value);
                        const addToClassResponse = await axios.post(`/web/ui/sc/addToClass?classId=${newlyCreatedClass.classId}`, userIds);

                        // Use the response from addToClass API to check if current user is in the class
                        const updatedClass = addToClassResponse.data;
                        const isCurrentUserInClass = updatedClass.userIds?.includes(user.userId);
                        const className = addClassFormValues.clazz.className;

                        if (isCurrentUserInClass) {
                            displayToastMessage(`New class added: ${className}`, null, 'success', null, 10000);
                        } else {
                            displayToastMessage(`New class added but you are not a teacher yet.`, `Find the class from the All Classes dropdown if you want to be a teacher of this class.`, 'log',null,10000);
                        }
                    } else {
                        setFormError('Class created successfully, but could not find it to add users.');
                    }
                } catch (userError) {
                    console.error('Failed to add users to class:', userError);
                    displayToastMessage(`New class added but failed to add your selected users.`, `Find the class from the All Classes dropdown to join the class and add users.`, 'error',null,10000);
                }
            } else {
                // No users selected - show the "find class" message
                displayToastMessage(`New class added but no users in it yet.`, `Find the class from the All Classes dropdown if you want to be a teacher of this class.`, 'success', null, 10000);
            }

            if (typeof callback === "function") {
                callback(response.data);
            }
            closeDialog();
        } catch (error) {
            if (error.response?.data?.message) {
                setFormError(error.response.data.message);
            } else if (!error.errorFields) {
                setFormError('An error occurred.');
            }
        }
    };

    const steps = [
        {
            title: 'Class Settings',
            content: (
                <div>
                    <Form
                        form={addClassForm}
                        layout="vertical"
                    >
                        <Form.Item
                            label="Class Name"
                            name={['clazz', 'className']}
                            rules={[
                                { required: true, message: 'Enter a Class Name' }
                            ]}
                            style={{marginBottom: 12}}
                        >
                            <Input />
                        </Form.Item>

                        {options?.calenderYears && (
                            <Form.Item
                                label="Calendar Year"
                                name={['clazz', 'calendarYear']}
                                style={{marginBottom: 12}}
                            >
                                <Select
                                    getPopupContainer={trigger => trigger.parentElement}
                                    options={options.calenderYears.map(y => ({value: y.value, label: y.label}))}
                                />
                            </Form.Item>
                        )}

                        {options?.schoolYears && (
                            <Form.Item
                                label="School Year"
                                name={['clazz', 'schoolYear']}
                                style={{marginBottom: 12}}
                            >
                                <Select
                                    getPopupContainer={trigger => trigger.parentElement}
                                    options={options.schoolYears.map(y => ({value: y.value, label: y.label})).reverse()}
                                />
                            </Form.Item>
                        )}

                        {brandConfig?.questionClusterMarkingTypes?.length > 1 && (
                            <Form.Item
                                label="Marking"
                                name={['clazz', 'markingType']}
                                style={{marginBottom: 12}}
                            >
                                <Select
                                    getPopupContainer={trigger => trigger.parentElement}
                                    options={brandConfig.questionClusterMarkingTypes.map(type => ({ value: type, label: type }))}
                                />
                            </Form.Item>
                        )}

                        {brandConfig?.allowHideWorkspaces && (
                            <Form.Item
                                name={['clazz', 'hideWorkspaces']}
                                valuePropName="checked"
                            >
                                <Checkbox>Hide Workspaces</Checkbox>
                            </Form.Item>
                        )}

                        {!brandConfig?.hideClassShowSolutions && (
                            <Form.Item
                                name={['clazz', 'showSolutions']}
                                valuePropName="checked"
                            >
                                <Checkbox>Show Answers / Suggested Responses</Checkbox>
                            </Form.Item>
                        )}

                        {!brandConfig?.hideClassShowWorkedSolutions && (
                            <Form.Item
                                name={['clazz', 'showWorkedSolutions']}
                                valuePropName="checked"
                            >
                                <Checkbox>Show Worked Solutions</Checkbox>
                            </Form.Item>
                        )}
                    </Form>

                    <div>
                        <Divider className={'ch-divider'} />
                        <Button
                            onClick={handleContinue}
                            type={'outline'}
                            style={{ width: '100%' }}
                        >
                            Next Step
                        </Button>
                    </div>
                </div>
            )
        },
        {
            title: `Add Users`,
            content: (
                <UserSelector
                    selectedUsers={selectedUsers}
                    onSelectedUsersChange={setSelectedUsers}
                    showAddButton={false}
                />
            )
        }
    ];

    return (
        <div className={'ch-drawer-right-content'}>
            {formError && (
                <Alert
                    style={{ marginBottom: 16 }}
                    message="Error"
                    description={formError}
                    type="error"
                    showIcon
                />
            )}
            <h2>Add a class</h2>

            <Steps
                current={currentStep}
                items={steps.map(item => ({ title: item.title }))}
                className={'ch-add-class-stepper-controls'}
            />

            <div>
                {steps[currentStep].content}
            </div>

            {currentStep === 1 &&
                <div className={'ch-manage-users-buttons-wrapper'}>
                    <Divider className={'ch-divider'} />
                    <div className={'ch-manage-users-buttons'}>
                        <Button
                            onClick={handleBack}
                            type={'outline'}
                        >
                            Back
                        </Button>
                        <Button
                            onClick={() => handleSave()}
                        >
                            Add Class {selectedUsers.length > 0 && `with ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}`}
                        </Button>
                    </div>
                </div>
            }
        </div>
    );
};

export default AddClassDrawer;